import type {AssignmentStatusCounts} from './hooks/useAssignmentStatusCounts';
import type {TeacherAssignmentRowFieldsFragment} from './queries/TeacherAssignments.queries.generated';

interface Args {
  assignmentCount: number;
  hasFiltersOn: boolean;
  /** The tab's own list query — the scope question only matters once it resolves empty. */
  isListLoading: boolean;
  counts?: AssignmentStatusCounts;
  isCountsLoading: boolean;
  hasCountsError: boolean;
}

interface AssignmentsScopeState {
  /** The scope-wide check is still resolving — show the list spinner, not a premature empty state. */
  isCheckingScope: boolean;
  /** The whole scope has no assignments at all — render the onboarding state. */
  isScopeEmpty: boolean;
}

/**
 * The design has a "no assignments yet" empty state for the whole scope — "scope" meaning all classes
 * (the cross-class view) or one class. To show it correctly we need to know whether the scope has zero
 * assignments across EVERY status, not just the active open/closed/... tab. The status selector's
 * totals already answer that: the four statuses partition every assignment (the status calc has a
 * closed catch-all), so their sum is the scope total — no separate existence query needed. This is
 * only consulted with filters off, where the counts are scope-pure (they otherwise follow filters).
 */
export const getAssignmentsScope = ({
  assignmentCount,
  hasFiltersOn,
  isListLoading,
  counts,
  isCountsLoading,
  hasCountsError
}: Args): AssignmentsScopeState => {
  // Only relevant when nothing is showing and no filter is narrowing the view.
  const isUndetermined = !hasFiltersOn && !isListLoading && assignmentCount === 0;

  // While a filter change refetches, `counts` still holds the previous variables' totals —
  // isCountsLoading gates that staleness out of the scope decision.
  const scopeTotal =
    !isCountsLoading && counts
      ? counts.open + counts.closed + counts.scheduled + counts.draft
      : undefined;

  return {
    isCheckingScope: isUndetermined && !hasCountsError && scopeTotal === undefined,
    // A failed count fetch must not masquerade as an empty scope (a false onboarding state); the
    // list's own hasError drives the retry panel instead.
    isScopeEmpty: isUndetermined && !hasCountsError && scopeTotal === 0
  };
};

interface GradeAffordanceArgs {
  assignment: TeacherAssignmentRowFieldsFragment;
  /** Drafts have nothing to grade. */
  isDraftSegment: boolean;
  isFrqGradingEnabled: boolean;
  isFrqFlagLoading: boolean;
}

// No submitted manually-graded responses stands in for the missing "has FRQs" field: no FRQs, or
// none turned in yet. It also keeps the button off MCQ-only rows, whose classroom assignments
// report gradesReleased: true because they have nothing to gate.
export const getGradeAffordance = ({
  assignment,
  isDraftSegment,
  isFrqGradingEnabled,
  isFrqFlagLoading
}: GradeAffordanceArgs): {
  gradedCount: number;
  totalCount: number;
  releasableCount: number;
} | null => {
  if (isDraftSegment || !isFrqGradingEnabled || isFrqFlagLoading) {
    return null;
  }
  // Undefined when the counts weren't requested, i.e. with the grading flag off.
  const totalCount = assignment.countOfSubmittedManuallyGradedGuesses;
  if (!totalCount) {
    return null;
  }
  const outstanding =
    (assignment.countOfUngradedGuesses ?? 0) + (assignment.countOfGradingInProgressGuesses ?? 0);
  return {
    gradedCount: Math.max(totalCount - outstanding, 0),
    totalCount,
    releasableCount: assignment.countOfReleasableGradedGuesses ?? 0
  };
};
